/**
 * Turning a picked image file into a small square avatar, in the browser.
 *
 * The profile photo is stored inline with the rest of the profile, so what
 * gets sent has to be small: a phone camera JPEG is several megabytes and is
 * read back on every page load. Squaring happens by centre crop — the middle
 * of a portrait is the face — and the re-encode is JPEG unless the source has
 * transparency, which PNG keeps and JPEG would fill with black.
 */

/** Edge length of the stored avatar. 256 covers the 80px settings tile and a
 *  2x display without storing anything anyone would call a photo library. */
export const AVATAR_PX = 256

/** Refused before decoding: past this the browser is being asked to decode a
 *  file no avatar needs, and the error is clearer than an out-of-memory. */
export const AVATAR_SOURCE_MAX_BYTES = 12 * 1024 * 1024

const JPEG_QUALITY = 0.82

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(new Error('The file could not be read'))
    reader.readAsDataURL(file)
  })
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('The image could not be decoded'))
    img.src = src
  })
}

/**
 * Centre-crop `file` to a square, scale it to AVATAR_PX and return a data URL.
 * Throws if the file is not a decodable image.
 */
export async function squareDataUrl(file: Blob, size = AVATAR_PX): Promise<string> {
  const source = await readAsDataUrl(file)
  const img = await loadImage(source)

  const edge = Math.min(img.naturalWidth || img.width, img.naturalHeight || img.height)
  if (!edge) throw new Error('The image has no dimensions')
  const sx = ((img.naturalWidth || img.width) - edge) / 2
  const sy = ((img.naturalHeight || img.height) - edge) / 2

  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('This browser cannot resize the image')
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(img, sx, sy, edge, edge, 0, 0, size, size)

  // PNG only where it earns its size: a source with an alpha channel would
  // lose its transparency to a JPEG's black fill.
  const keepsAlpha = file.type === 'image/png' || file.type === 'image/webp'
  return keepsAlpha && hasTransparency(ctx, size)
    ? canvas.toDataURL('image/png')
    : canvas.toDataURL('image/jpeg', JPEG_QUALITY)
}

function hasTransparency(ctx: CanvasRenderingContext2D, size: number): boolean {
  try {
    const { data } = ctx.getImageData(0, 0, size, size)
    for (let i = 3; i < data.length; i += 4 * 16) {
      if (data[i] < 255) return true
    }
    return false
  } catch {
    // A tainted canvas cannot be read; treat it as opaque rather than failing.
    return false
  }
}
